import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CreditType,
  ExpenseType,
  Transaction,
  TransactionStatus,
  TransactionType,
} from "../../types/transaction.type";
import { api } from "../../api/api";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Popconfirm,
  Popover,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Typography,
  Checkbox,
} from "antd";
import useGetRequest from "../../hooks/useGetRequest";
import { User, UserRole } from "../../types/user.type";
import { Worker } from "../../types/worker.type";
import { ClientType } from "../../types/client.type";
import { DownOutlined, PrinterOutlined, SettingOutlined } from "@ant-design/icons";
import { useAuth } from "../../contexts/auth.context";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { canEdit } from "../../utils/roleUtils";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { Machine } from "../../types/machine.type";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import dayjs from "dayjs";
import { useExchangeRate } from "../../contexts/exchangeRate.context";
import { IMeterReading } from "../../types/meterReading.type";

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const defaultVisibleColumns = {
  transactionId: true,
  date: true,
  status: true,
  cashboxName: true,
  cashierName: true,
  description: true,
  currency: true,
  balanceBefore: true,
  balanceAfter: true,
  amount: true,
  profit: true,
  exchangeRate: true,
  type: true,
  creditType: true,
  expenseType: true,
  workerName: true,
  clientName: true,
  machineName: true,
  partnerName: true,
  salaryDebtBefore: true,
  salaryDebtAfter: true,
  actions: true,
};

const loadColumnVisibility = (): Record<string, boolean> => {
  try {
    const saved = localStorage.getItem('transactions-column-visibility');
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...defaultVisibleColumns, ...parsed };
    }
  } catch (error) {
    console.warn('Failed to load column visibility from localStorage:', error);
  }
  return defaultVisibleColumns;
};

export default function Transactions() {
  const { authState } = useAuth();
  const { itemId, type, name, currency } = useParams();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [transactionType, setTransactionType] = useState<
    TransactionType | undefined
  >(undefined);
  const [creditType, setCreditType] = useState<CreditType | undefined>(
    undefined
  );
  const [clientType, setClientType] = useState<ClientType | undefined>(
    undefined
  );
  const [expenseType, setExpenseType] = useState<ExpenseType | undefined>(
    undefined
  );
  const [expenseId, setExpenseId] = useState<string | undefined>(undefined);
  const [cashierId, setCashierId] = useState<string | undefined>(undefined);
  const [machineId, setMachineId] = useState<string | undefined>(undefined);
  const [noMachine, setNoMachine] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [isAddMachineOpen, setIsAddMachineOpen] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const [totalAmountUSD, setTotalAmountUSD] = useState<number>(0);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>(
    []
  );
  const [status, setStatus] = useState<TransactionStatus | undefined>(
    undefined
  );
  const [machineForm] = Form.useForm();
  const [visibleColumns, setVisibleColumns] =
    useState<Record<string, boolean>>(loadColumnVisibility);
  const { exchangeRate } = useExchangeRate();
  const { data: expenseList, loading: expenseListLoading } =
    useGetRequest("/expense");
  const { data: cashiersList, loading: cashiersListLoading } =
    useGetRequest("/user/cashiers");

  const { data: workerList, loading: workersListLoading } =
    useGetRequest("/worker/list");

  const { data: machineList, loading: machineListLoading } =
    useGetRequest("/machine/list");

  const { data: meterReadingList, loading: meterReadingListLoading } =
    useGetRequest("/meter-readings");

  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >([dayjs().startOf("month"), dayjs().endOf("month")]);

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/${type}/${itemId}/transactions`, {
        params: {
          page,
          limit: pageSize,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
          type: transactionType,
          creditType,
          expenseId,
          expenseType,
          cashierId,
          machineId,
          clientType,
          noMachine,
          search: searchTerm,
          status,
        },
      });
      setTransactions(response.data.transactions);
      setTotal(response.data.total);
      setTotalAmountUSD(response.data.totalAmountUSD);
    } catch (error) {
      console.error("Ошибка получение транзакций", error);
      message.error("Ошибка получение транзакций");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTransactions = async () => {
    setConfirmLoading(true);

    try {
      await api.post("/cashbox/complete-transactions", {
        transactionIds: selectedTransactions,
      });
      message.success("Транзакции успешно завершены.");

      fetchTransactions();
      setIsModalOpen(false);
    } catch (error: any) {
      console.error("Error saving transaction", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
      setSelectedTransactions([]);
    }
  };

  const handleReviewTransactions = async () => {
    setConfirmLoading(true);

    try {
      await api.post("/cashbox/review-transactions", {
        transactionIds: selectedTransactions,
      });
      message.success("Транзакции успешно проверены.");

      fetchTransactions();
      setIsModalOpen(false);
    } catch (error: any) {
      console.error("Error saving transaction", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
      setSelectedTransactions([]);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [
    page,
    pageSize,
    dateRange,
    transactionType,
    creditType,
    expenseId,
    expenseType,
    cashierId,
    machineId,
    itemId,
    clientType,
    noMachine,
    searchTerm,
    status,
  ]);

  useEffect(() => {
    if (exchangeRate) {
      form.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, form]);

  useEffect(() => {
    try {
      localStorage.setItem(
        'transactions-column-visibility',
        JSON.stringify(visibleColumns)
      );
    } catch (error) {
      console.warn('Failed to save column visibility to localStorage:', error);
    }
  }, [visibleColumns]);

  const handleColumnVisibilityChange = (key: string, checked: boolean) => {
    setVisibleColumns(prev => ({
      ...prev,
      [key]: checked,
    }));
  };

  const handleSelectAllColumns = () => {
    const newVisibility: Record<string, boolean> = {};
    allColumns.forEach(col => {
      if (col.key) {
        newVisibility[col.key] = true;
      }
    });
    setVisibleColumns(newVisibility);
  };

  const handleDeselectAllColumns = () => {
    const newVisibility: Record<string, boolean> = {};
    allColumns.forEach(col => {
      if (col.key) {
        newVisibility[col.key] = false;
      }
    });
    setVisibleColumns(newVisibility);
  };

  const combinedExpenseOptions = [
    ...Object.entries(ExpenseType).map(([key, value]) => ({
      key,
      value,
      label: value,
      isStatic: true,
    })),
    ...(expenseList?.map((expense: { _id: any; name: any }) => ({
      key: expense._id,
      value: expense.name,
      label: expense.name,
      isStatic: false,
    })) || []),
  ];

  const handleExpenseChange = (value: string) => {
    const selectedOption = combinedExpenseOptions.find(
      (option) => option.value === value
    );
    if (selectedOption) {
      if (selectedOption.isStatic) {
        setExpenseType(value as ExpenseType);
        setExpenseId(undefined);
      } else {
        setExpenseId(selectedOption.key);
        setExpenseType(undefined);
      }
    }
  };

  const handleExport = () => {
    const exportParams: { [key: string]: any } = {
      startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
      endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
      type: transactionType,
      creditType,
      expenseId,
      expenseType,
      cashierId,
    };

    if (type === "client") {
      exportParams.clientId = itemId;
    } else if (type === "cashbox" && itemId !== "all") {
      exportParams.cashboxId = itemId;
    }

    downloadFile(
      "/export/transactions",
      "transactions_data.xlsx",
      exportParams
    );
  };

  const handleDeleteTransaction = async (id: string) => {
    try {
      await api.delete(`/cashbox/${id}/transaction`);

      message.success("Транзакция успешно удалена.");
      fetchTransactions();
    } catch (error: any) {
      message.error(error.response.data.error);
    }
  };

  const handleAddMachine = async (values: { machineId: string }) => {
    setConfirmLoading(true);
    try {
      await api.post(
        `/cashbox/${selectedTransaction?._id}/add-machine`,
        values
      );

      message.success("Станок успешно прикреплен.");
      setIsAddMachineOpen(false);

      fetchTransactions();
      setSelectedTransaction(null);
      machineForm.resetFields();
    } catch (error: any) {
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const allColumns = [
    {
      title: "ID",
      dataIndex: "transactionId",
      key: "transactionId",
      render: (transactionId: number) => (
        <Typography.Text type="secondary">
          {transactionId}
        </Typography.Text>
      ),
    },
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Касса",
      dataIndex: "cashboxName",
      key: "cashboxName",
    },
    {
      title: "Кассир",
      dataIndex: "cashierName",
      key: "cashierName",
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
      render: (description: string) => (
        <div style={{ 
          whiteSpace: 'pre-wrap', 
          wordBreak: 'break-word',
          maxWidth: '550px',
          lineHeight: '1.4'
        }}>
          {description}
        </div>
      ),
    },
    {
      title: "Валюта",
      dataIndex: "currency",
      key: "currency",
    },
    {
      title: "Баланс до",
      dataIndex: "balanceBefore",
      key: "balanceBefore",
      render: (data: number) => roundToThreeDecimals(data),
    },
    {
      title: "Баланс после",
      dataIndex: "balanceAfter",
      key: "balanceAfter",
      render: (data: number) => roundToThreeDecimals(data),
    },
    {
      title: "Сумма",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => roundToThreeDecimals(amount),
    },
    {
      title: "Доход",
      dataIndex: "profit",
      key: "profit",
      render: (amount: number) => roundToThreeDecimals(amount),
    },
    {
      title: "Курс",
      dataIndex: "exchangeRate",
      key: "exchangeRate",
    },
    {
      title: "Тип",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Тип прихода",
      dataIndex: "creditType",
      key: "creditType",
    },
    {
      title: "Тип расхода",
      dataIndex: "expenseType",
      key: "expenseType",
    },
    {
      title: "Имя сотрудника",
      dataIndex: "workerName",
      key: "workerName",
    },
    {
      title: "Имя клиента",
      dataIndex: "clientName",
      key: "clientName",
    },
    {
      title: "Имя станка",
      dataIndex: "machineName",
      key: "machineName",
    },
    {
      title: "Имя партнёра",
      dataIndex: "partnerName",
      key: "partnerName",
    },
    {
      title: "Зарплата долг до",
      dataIndex: "salaryDebtBefore",
      key: "salaryDebtBefore",
      render: (data: number) => roundToThreeDecimals(data),
    },
    {
      title: "Зарплата долг после",
      dataIndex: "salaryDebtAfter",
      key: "salaryDebtAfter",
      render: (data: number) => roundToThreeDecimals(data),
    },
    {
      title: "",
      key: "actions",
      fixed: "right" as "right",
      render: (_: any, transaction: Transaction) => {
        const menu = (
          <Menu>
            <Menu.Item onClick={() => handleEditClick(transaction)}>
              Изменить
            </Menu.Item>
            {canEdit(authState.user?.role) && (
              <>
                {transaction.type !== TransactionType.CASHTRANSFER && (
                  <Menu.Item>
                    <Popconfirm
                      title="Вы уверены, что хотите удалить эту транзакцию?"
                      onConfirm={() => handleDeleteTransaction(transaction._id)}
                      okText="Да"
                      cancelText="Нет"
                    >
                      Удалить
                    </Popconfirm>
                  </Menu.Item>
                )}
                <Menu.Item onClick={() => handleAddMachineClick(transaction)}>
                  Прикрепить станок
                </Menu.Item>
              </>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const filteredColumns = allColumns.filter(
    column => column.key && visibleColumns[column.key]
  );

  const columnSelectorContent = (
    <div style={{ width: 300, maxHeight: 400, overflowY: 'auto' }}>
      <div
        style={{
          marginBottom: 16,
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: 8,
        }}
      >
        <Space>
          <Button size='small' onClick={handleSelectAllColumns}>
            Выбрать все
          </Button>
          <Button size='small' onClick={handleDeselectAllColumns}>
            Снять все
          </Button>
        </Space>
      </div>
      {allColumns.map(
        column =>
          column.key && (
            <div key={column.key} style={{ marginBottom: 8 }}>
              <Checkbox
                checked={visibleColumns[column.key]}
                onChange={e =>
                  handleColumnVisibilityChange(column.key, e.target.checked)
                }
              >
                {column.title}
              </Checkbox>
            </div>
          )
      )}
    </div>
  );

  const handleTransactionTypeChange = (value: TransactionType) => {
    form.setFieldsValue({ creditOrExpenseType: undefined });
    setTransactionType(value);
  };

  const handleSubmitForm = async (values: Partial<Transaction>) => {
    setConfirmLoading(true);
    const data = {
      ...values,
      exchangeRate: exchangeRate || values.exchangeRate,
      cashboxId: itemId,
      expenseType,
      expenseId,
    };

    try {
      if (expenseType === ExpenseType.METER_READING) {
        await api.post(`/meter-readings/record`, data);
      } else {
        await api.post(`/cashbox/${itemId}/transaction`, data);
      }
      message.success("Тарнзакция успешно добавлена.");
      form.resetFields();
      setTransactionType(undefined);
      setCreditType(undefined);
      setExpenseType(undefined);
      setExpenseId(undefined);
      fetchTransactions();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving transaction:", error);
      message.error(error.response.data.error);
    }
  };

  const handleEdit = async (values: { description: string }) => {
    setConfirmLoading(true);
    try {
      await api.put(`/cashbox/${selectedTransaction?._id}/transaction`, values);

      message.success("Транзакция успешно обновлена");

      setIsEditOpen(false);
      setConfirmLoading(false);
      fetchTransactions();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving transaction:", error);
      message.error(error.response.data.error);
    }
  };

  const handleEditClick = (transaction: Transaction) => {
    setIsEditOpen(true);
    setSelectedTransaction(transaction);
    editForm.setFieldsValue(transaction);
  };

  const handleAddMachineClick = (transaction: Transaction) => {
    setIsAddMachineOpen(true);
    setSelectedTransaction(transaction);
    machineForm.setFieldsValue(transaction);
  };

  const rowSelection = {
    selectedRowKeys: selectedTransactions,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedTransactions(selectedRowKeys as string[]);
    },
  };

  const rowClassName = (record: Transaction) => {
    if (canEdit(authState.user?.role) || authState.user?.role === "director") {
      if (record.status === TransactionStatus.COMPLETED) {
        return "row-orange";
      } else if (record.status === TransactionStatus.PENDING) {
        return "row-yellow";
      } else if (record.status === TransactionStatus.REVIEWED) {
        return "row-green";
      }

      return "";
    } else {
      if (record.status === TransactionStatus.COMPLETED) {
        return "row-green";
      } else if (record.status === TransactionStatus.PENDING) {
        return "row-yellow";
      }
      return "";
    }
  };

  const handlePrintTransactions = async () => {
    const selectedData = transactions.filter((transaction) =>
      selectedTransactions.includes(transaction._id)
    );

    const printContent = `
      <html>
        <head>
          <title>Транзакции</title>
          <style>
            @page {
              size: landscape;
              margin: 10mm;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 10px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 9px;
              border: 2px solid #000;
            }
            th, td {
              border: 1px solid #000;
              padding: 4px;
              text-align: left;
              white-space: nowrap;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
              border-bottom: 2px solid #000;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
          </style>
        </head>
        <body>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Дата</th>
                <th>Статус</th>
                <th>Касса</th>
                <th>Кассир</th>
                <th>Описание</th>
                <th>Валюта</th>
                <th>Баланс до</th>
                <th>Баланс после</th>
                <th>Сумма</th>
                <th>Тип</th>
                <th>Тип прихода</th>
                <th>Тип расхода</th>
              </tr>
            </thead>
            <tbody>
              ${selectedData
                .map(
                  (transaction) => `
                <tr>
                  <td>${transaction.transactionId}</td>
                  <td>${transaction.date}</td>
                  <td>${transaction.status}</td>
                  <td>${transaction.cashboxName || "-"}</td>
                  <td>${transaction.cashierName || "-"}</td>
                  <td>${transaction.description}</td>
                  <td>${transaction.currency}</td>
                  <td>${roundToThreeDecimals(transaction.balanceBefore)}</td>
                  <td>${roundToThreeDecimals(transaction.balanceAfter)}</td>
                  <td>${roundToThreeDecimals(transaction.amount)}</td>
                  <td>${transaction.type}</td>
                  <td>${transaction.creditType || "-"}</td>
                  <td>${transaction.expenseType || "-"}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printFrame = document.createElement("iframe");
    printFrame.style.display = "none";
    document.body.appendChild(printFrame);

    const frameDoc = printFrame.contentWindow?.document;
    if (frameDoc) {
      frameDoc.open();
      frameDoc.write(printContent);
      frameDoc.close();

      printFrame.contentWindow?.focus();
      printFrame.contentWindow?.print();
      await handleCompleteTransactions();

      setTimeout(() => {
        document.body.removeChild(printFrame);
      }, 1000);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Транзакции {name}</Title>
      <Row gutter={16}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Сумма"
              value={roundToThreeDecimals(totalAmountUSD)}
            />
          </Card>
        </Col>
      </Row>
      <Space style={{ marginBottom: 16, flexWrap: "wrap" }}>
        <Input.Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />
        <RangePicker
          onChange={handleRangeChange}
          placeholder={["Начало", "Конец"]}
          format="DD.MM.YY"
          value={dateRange}
        />
        <Select
          placeholder="Фильтр по статусу"
          onChange={(value) => setStatus(value)}
          allowClear
          style={{ width: 200 }}
        >
          {Object.values(TransactionStatus).map((status) => (
            <Option key={status} value={status}>
              {status}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Фильтр по кассиру"
          onChange={(value) => setCashierId(value)}
          allowClear
          style={{ width: 200 }}
          loading={cashiersListLoading}
        >
          {cashiersList?.map((cashier: User) => (
            <Option key={cashier._id} value={cashier._id}>
              {cashier.name}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Фильтр по типу клиента"
          onChange={(value) => setClientType(value)}
          allowClear
          style={{ width: 200 }}
        >
          {Object.values(ClientType).map((type) => (
            <Option key={type} value={type}>
              {type}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Фильтр по типу транзакции"
          onChange={handleTransactionTypeChange}
          allowClear
          onClear={() => {
            setCreditType(undefined);
            setExpenseId(undefined);
            setExpenseType(undefined);
          }}
          style={{ width: 200 }}
        >
          {Object.values(TransactionType).map((type) => (
            <Option key={type} value={type}>
              {type}
            </Option>
          ))}
        </Select>
        {transactionType === TransactionType.CREDIT && (
          <Select
            placeholder="Фильтр по типу прихода"
            onChange={(value) => setCreditType(value)}
            allowClear
            style={{ width: 200 }}
          >
            {Object.values(CreditType).map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
        )}
        <Select
          placeholder="Фильтр по станку"
          onChange={(value) => setMachineId(value)}
          allowClear
          onClear={() => setMachineId(undefined)}
          style={{ width: 200 }}
          loading={machineListLoading}
        >
          {machineList?.map((machine: Machine) => (
            <Option key={machine._id} value={machine._id}>
              {machine.name}
            </Option>
          ))}
        </Select>

        <Select
          placeholder="Фильтр по типу расхода"
          onChange={handleExpenseChange}
          allowClear
          onClear={() => setExpenseId(undefined)}
          style={{ width: 200 }}
          loading={expenseListLoading}
        >
          {combinedExpenseOptions.map((option) => (
            <Option key={option.key} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>

        <Checkbox
          checked={noMachine}
          onChange={(e) => setNoMachine(e.target.checked)}
        >
          Без станка
        </Checkbox>

        <Popover
          content={columnSelectorContent}
          title='Выбор колонок'
          trigger='click'
          placement='bottomLeft'
        >
          <Button icon={<SettingOutlined />}>Колонки</Button>
        </Popover>

        {(canEdit(authState.user?.role) ||
          authState.user?.role === UserRole.Cashier ||
          authState.user?.role === UserRole.Director) &&
          itemId !== "all" && (
            <>
              <Button type="primary" onClick={() => setIsModalOpen(true)}>
                Добавить
              </Button>
            </>
          )}

        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
        
        {/* Кнопки действий только для админа и кассиров */}
        {canEdit(authState.user?.role) && (
          <>
            <Button
              type="primary"
              onClick={handleCompleteTransactions}
              disabled={selectedTransactions.length === 0}
              loading={confirmLoading}
            >
              Завершить
            </Button>
            <Button
              type="primary"
              onClick={handleReviewTransactions}
              disabled={selectedTransactions.length === 0}
              loading={confirmLoading}
            >
              Проверка (АДМИН)
            </Button>
          </>
        )}
        
        <Button
          type="default"
          icon={<PrinterOutlined />}
          onClick={handlePrintTransactions}
          disabled={selectedTransactions.length === 0}
        >
          Печать
        </Button>
      </Space>
      <Table
        columns={filteredColumns}
        dataSource={transactions}
        loading={loading}
        rowKey="_id"
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (page, pageSize) => {
            setPage(page);
            setPageSize(pageSize);
          },
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100", "1000"],
        }}
        scroll={{ x: "max-content" }}
        rowSelection={rowSelection}
        rowClassName={rowClassName}
      />

      <Modal
        title="Добавить транзакцию"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item
            name="type"
            label="Тип транзакции"
            rules={[{ required: true, message: "Выберете тип транзакции" }]}
          >
            <Select placeholder="Выберете тип транзакции" allowClear>
              {Object.values(TransactionType)
                .slice(0, 2)
                .map((type) => (
                  <Option key={type} value={type}>
                    {type}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="machineId"
            label="Станок"
            rules={[{ required: true, message: "Выберите станок" }]}
          >
            <Select
              placeholder="Выберите станок"
              allowClear
              loading={machineListLoading}
              showSearch
              filterOption={(input, option) => {
                const optionText = option?.label || option?.children;
                return String(optionText).toLowerCase().includes(input.toLowerCase());
              }}
              optionFilterProp="children"
            >
              {machineList?.map((machine: Partial<Machine>) => (
                <Option key={machine._id} value={machine._id}>
                  {machine.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {transactionType === TransactionType.CREDIT && (
            <Form.Item
              name="creditType"
              label="Тип прихода"
              rules={[{ required: true, message: "Выберите тип прихода" }]}
            >
              <Select placeholder="Выберите тип прихода" allowClear>
                {Object.values(CreditType)
                  .slice(-2)
                  .map((type) => (
                    <Option key={type} value={type}>
                      {type}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          )}
          {transactionType !== TransactionType.CREDIT && (
            <Form.Item
              name="expenseType"
              label="Тип расхода"
              rules={[{ required: true, message: "Выберите тип расхода" }]}
            >
              <Select
                placeholder="Выберите тип расхода"
                onChange={handleExpenseChange}
                allowClear
              >
                {combinedExpenseOptions.slice(4).map((option) => (
                  <Option key={option.key} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {expenseType === ExpenseType.SALARY && (
            <Form.Item
              name="workerId"
              label="Сотрудник"
              rules={[{ required: true, message: "Выберите сотрудника" }]}
            >
              <Select
                placeholder="Выберите сотрудника"
                allowClear
                loading={workersListLoading}
                showSearch
                filterOption={(input, option) => {
                  const optionText = option?.label || option?.children;
                  return String(optionText).toLowerCase().includes(input.toLowerCase());
                }}
                optionFilterProp="children"
              >
                {workerList.map((worker: Partial<Worker>) => (
                  <Option key={worker._id} value={worker._id}>
                    {worker.name} {worker.surname} {worker.nickname ? `(${worker.nickname})` : ''} {worker.salaryCurrency}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {expenseType === ExpenseType.METER_READING && (
            <>
              <Form.Item
                name="meterReadingId"
                label="Счетчик"
                rules={[{ required: true, message: "Выберите счетчик" }]}
              >
                <Select
                  placeholder="Выберите счетчик"
                  allowClear
                  loading={workersListLoading}
                  showSearch
                  filterOption={(input, option) => {
                    const optionText = option?.label || option?.children;
                    return String(optionText).toLowerCase().includes(input.toLowerCase());
                  }}
                  optionFilterProp="children"
                >
                  {meterReadingList.map(
                    (meterReading: Partial<IMeterReading>) => (
                      <Option key={meterReading._id} value={meterReading._id}>
                        {meterReading.name}
                      </Option>
                    )
                  )}
                </Select>
              </Form.Item>

              <Form.Item
                name="reading"
                label="Показание"
                rules={[{ required: true, message: "Введите показание" }]}
              >
                <InputNumber />
              </Form.Item>
            </>
          )}
          <Form.Item
            name="amount"
            label="Сумма"
            rules={[{ required: true, message: "Введите сумму" }]}
          >
            <InputNumber />
          </Form.Item>
          <Form.Item
            name="exchangeRate"
            label="Курс"
            rules={[{ required: true, message: "Введите курс" }]}
            hidden={true}
          >
            <InputNumber style={{ width: "100%" }} readOnly />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: "Введите описание." }]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Изменить"
        open={isEditOpen}
        onCancel={() => setIsEditOpen(false)}
        confirmLoading={confirmLoading}
        onOk={() => editForm.submit()}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item
            name="type"
            label="Тип транзакции"
            rules={[{ required: true, message: "Выберете тип транзакции" }]}
          >
            <Select placeholder="Выберете тип транзакции" allowClear>
              {Object.values(TransactionType)
                .filter((_, index, arr) => index !== arr.length - 1)
                .map((type) => (
                  <Option key={type} value={type}>
                    {type}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item name="machineId" label="Станок">
            <Select
              placeholder="Выберите станок"
              allowClear
              loading={machineListLoading}
              showSearch
              filterOption={(input, option) => {
                const optionText = option?.label || option?.children;
                return String(optionText).toLowerCase().includes(input.toLowerCase());
              }}
              optionFilterProp="children"
            >
              {machineList?.map((machine: Partial<Machine>) => (
                <Option key={machine._id} value={machine._id}>
                  {machine.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="creditType" label="Тип прихода">
            <Select placeholder="Выберите тип прихода" allowClear>
              {Object.values(CreditType)
                .slice(-2)
                .map((type) => (
                  <Option key={type} value={type}>
                    {type}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item name="expenseType" label="Тип расхода">
            <Select placeholder="Выберите тип расхода" allowClear>
              {combinedExpenseOptions.slice(5).map((option) => (
                <Option key={option.key} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: "Введите описание." }]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Прикрепить станок"
        open={isAddMachineOpen}
        onCancel={() => setIsAddMachineOpen(false)}
        confirmLoading={confirmLoading}
        onOk={() => machineForm.submit()}
      >
        <Form form={machineForm} layout="vertical" onFinish={handleAddMachine}>
          <Form.Item
            name="machineId"
            label="Станок"
            rules={[{ required: true, message: "Выберите станок" }]}
          >
            <Select
              placeholder="Выберите станок"
              allowClear
              loading={machineListLoading}
              showSearch
              filterOption={(input, option) => {
                const optionText = option?.label || option?.children;
                return String(optionText).toLowerCase().includes(input.toLowerCase());
              }}
              optionFilterProp="children"
            >
              {machineList?.map((machine: Partial<Machine>) => (
                <Option key={machine._id} value={machine._id}>
                  {machine.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
